/**
 * D8b: matterType-aware vocabulary for the case-level unit.
 *
 * ⚠ VERBATIM BEHAVIORAL PORT of `@lexiun/lx-kernel`'s
 * `src/models/case-labels.ts` — the CANONICAL home since the ontology
 * plan D-9 promotion (2026-07-14). lx-kernel is a Node package
 * (better-sqlite3 / mongodb) that does not bundle into this browser SPA,
 * so — exactly as `lib/workspace/display-name.ts` mirrors lx-kernel's
 * `display-meta.ts` — this file keeps a browser-safe copy of the `CASE_LABELS`
 * table + the pure resolvers (`bcp47ToShort` / `normalizeMatterType` /
 * `caseLabelFor` / `caseLabelForms`) and adds the React `useCaseLabel` hooks
 * (lx-kernel is React-free). KEEP IN SYNC — the lextools ontology-kernel CI
 * guard already pins the kernel table against lx-kernel's `CASE_LABELS`, so
 * the authority chain (matter-type registry → lx-kernel `CASE_LABELS` → the
 * kernel prompt table) is CI-guarded; this SPA copy is the one manual-sync
 * leaf. Cross-checked identical against the lx-kernel source 2026-07-14.
 *
 * Internal code always calls the leaf entity a "Case" (the kernel
 * `CaseSchema`, `case.*` RPCs, `case_*` testids, etc.). User-facing
 * copy gets the matterType-appropriate label through this helper:
 *
 *   - civil      → Ação Cível / Ações Cíveis                         (PT)  · Civil Case / Civil Cases     · Demanda Civil / Demandas Civiles
 *   - labor      → Reclamação Trabalhista / Reclamações Trabalhistas (PT)  · Labor Case / Labor Cases     · Demanda Laboral / Demandas Laborales
 *   - tax        → Contencioso Tributário / Contenciosos Tributários (PT)  · Tax Case / Tax Cases         · Contencioso Tributario / Contenciosos Tributarios
 *   - contract   → Contrato / Contratos                               (PT)  · Contract / Contracts         · Contrato / Contratos
 *   - advisory   → Consulta / Consultas                               (PT)  · Advisory / Advisories        · Consulta / Consultas
 *   - corporate  → Operação Societária / Operações Societárias       (PT)  · Corporate Deal / Deals       · Operación Societaria / Operaciones Societarias
 *   - realestate → Operação Imobiliária / Operações Imobiliárias      (PT)  · Real Estate Deal / Deals     · Operación Inmobiliaria / Operaciones Inmobiliarias
 *   - compliance → Revisão de Compliance / Revisões de Compliance    (PT)  · Compliance Review / Reviews  · Revisión de Cumplimiento / Revisiones de Cumplimiento
 *   - other      → Trabalho / Trabalhos                               (PT)  · Work item / Work items       · Trabajo / Trabajos
 *
 * Rationale: see [ai-docs/lexide-broadening-plan.md §matterType tab
 * registry + caseLabelFor migration]. Solo practitioners in Brazil
 * handle heterogeneous matter types for the same client, so the case
 * label must track the parent matter's workflow shape rather than
 * being hardcoded to "Caso".
 *
 * Every LexIDE component that renders a visible "Case" or "Caso"
 * string calls this helper instead of a literal string. Adding a new
 * matterType means adding its row in the lx-kernel canonical
 * (`@lexiun/lx-kernel` `src/models/case-labels.ts`) first, then mirroring
 * it here — the guards prove the prompt tables follow.
 *
 * Phase B.1B (W4): added `es` translations + a `useCaseLabel` hook
 * that reads the active locale from `useLanguageStore`. The pure
 * `caseLabelFor(value, locale, count?)` function is preserved for
 * non-React contexts (tests, utility modules). Callers in React
 * components should migrate to `useCaseLabel` so the rendered text
 * updates on locale change automatically.
 */

import { useLanguageStore } from "@/stores/useLanguageStore";
import type { LanguagePreference } from "@/lib/language";

export type MatterType =
  | "civil"
  | "labor"
  | "tax"
  | "contract"
  | "advisory"
  | "corporate"
  | "realestate"
  | "compliance"
  | "other";

/** Short locale codes used in the lookup table. Inferred from a
 *  BCP-47 tag via `bcp47ToShort` below. */
export type CaseLabelLocale = "pt" | "en" | "es";

export interface CaseLabelForm {
  singular: string;
  plural: string;
}

export type CaseLabelTable = Record<MatterType, Record<CaseLabelLocale, CaseLabelForm>>;

export const CASE_LABELS: CaseLabelTable = {
  civil: {
    pt: { singular: "Ação Cível", plural: "Ações Cíveis" },
    en: { singular: "Civil Case", plural: "Civil Cases" },
    es: { singular: "Demanda Civil", plural: "Demandas Civiles" },
  },
  labor: {
    pt: { singular: "Reclamação Trabalhista", plural: "Reclamações Trabalhistas" },
    en: { singular: "Labor Case", plural: "Labor Cases" },
    es: { singular: "Demanda Laboral", plural: "Demandas Laborales" },
  },
  tax: {
    pt: { singular: "Contencioso Tributário", plural: "Contenciosos Tributários" },
    en: { singular: "Tax Case", plural: "Tax Cases" },
    es: { singular: "Contencioso Tributario", plural: "Contenciosos Tributarios" },
  },
  contract: {
    pt: { singular: "Contrato", plural: "Contratos" },
    en: { singular: "Contract", plural: "Contracts" },
    es: { singular: "Contrato", plural: "Contratos" },
  },
  advisory: {
    pt: { singular: "Consulta", plural: "Consultas" },
    en: { singular: "Advisory", plural: "Advisories" },
    es: { singular: "Consulta", plural: "Consultas" },
  },
  corporate: {
    pt: { singular: "Operação Societária", plural: "Operações Societárias" },
    en: { singular: "Corporate Deal", plural: "Corporate Deals" },
    es: { singular: "Operación Societaria", plural: "Operaciones Societarias" },
  },
  realestate: {
    pt: { singular: "Operação Imobiliária", plural: "Operações Imobiliárias" },
    en: { singular: "Real Estate Deal", plural: "Real Estate Deals" },
    es: { singular: "Operación Inmobiliaria", plural: "Operaciones Inmobiliarias" },
  },
  compliance: {
    pt: { singular: "Revisão de Compliance", plural: "Revisões de Compliance" },
    en: { singular: "Compliance Review", plural: "Compliance Reviews" },
    es: { singular: "Revisión de Cumplimiento", plural: "Revisiones de Cumplimiento" },
  },
  other: {
    pt: { singular: "Trabalho", plural: "Trabalhos" },
    en: { singular: "Work item", plural: "Work items" },
    es: { singular: "Trabajo", plural: "Trabajos" },
  },
};

/**
 * Map a BCP-47 tag to the short code used in the lookup table.
 * Unrecognized tags fall back to `"pt"` (matches the historical
 * default before es was added).
 */
export function bcp47ToShort(tag: LanguagePreference | string): CaseLabelLocale {
  if (tag.startsWith("en")) return "en";
  if (tag.startsWith("es")) return "es";
  return "pt";
}

/**
 * Normalize an unknown matter-type string to the canonical enum.
 * Unknown/absent values fall back to `"civil"` — the default
 * D5a-era shape. Exported so sibling modules (matter-tab-registry)
 * can reuse the normalization rule.
 */
export function normalizeMatterType(matterType: string | undefined): MatterType {
  if (
    matterType === "civil" ||
    matterType === "labor" ||
    matterType === "tax" ||
    matterType === "contract" ||
    matterType === "advisory" ||
    matterType === "corporate" ||
    matterType === "realestate" ||
    matterType === "compliance" ||
    matterType === "other"
  ) {
    return matterType;
  }
  // Back-compat aliases for renamed matter types (2026-04-17).
  if (matterType === "litigation") return "civil";
  if (matterType === "transactional") return "corporate";
  // Unknown/absent matter types fall back to civil — the default
  // D5a-era shape. A future lint could reject this silently; for now,
  // falling back is the right call because pre-D5b cases lack matterType.
  return "civil";
}

/**
 * Resolve the user-facing label for a matter's case-level unit.
 *
 * `count` optional — if `count === 1` returns the singular form,
 * otherwise plural. `count === undefined` defaults to singular.
 *
 * `locale` accepts either the short code (`"pt"` / `"en"` / `"es"`)
 * or a BCP-47 tag (`"pt-BR"` / `"en-US"` / `"es-ES"`); the latter is
 * normalised via `bcp47ToShort`. Defaults to `"pt"` for back-compat
 * with pre-Phase-B.1B callers.
 *
 * @example
 *   caseLabelFor("contract", "pt")          // "Contrato"
 *   caseLabelFor("contract", "pt", 3)       // "Contratos"
 *   caseLabelFor("advisory", "en", 1)       // "Advisory"
 *   caseLabelFor("contract", "pt-BR")       // "Contrato" (BCP-47 normalised)
 *   caseLabelFor(undefined, "pt")           // "Ação Cível" (civil fallback)
 */
export function caseLabelFor(
  matterType: string | undefined,
  locale: CaseLabelLocale | LanguagePreference | string = "pt",
  count?: number,
): string {
  const type = normalizeMatterType(matterType);
  const shortLocale: CaseLabelLocale =
    locale === "pt" || locale === "en" || locale === "es"
      ? locale
      : bcp47ToShort(locale);
  const form = CASE_LABELS[type][shortLocale];
  return count !== undefined && count !== 1 ? form.plural : form.singular;
}

/**
 * Return both singular and plural forms for a matter type. Useful
 * when a component needs to render both (e.g., "1 Caso" vs "3 Casos"
 * with the count injected at display time).
 */
export function caseLabelForms(
  matterType: string | undefined,
  locale: CaseLabelLocale | LanguagePreference | string = "pt",
): CaseLabelForm {
  const type = normalizeMatterType(matterType);
  const shortLocale: CaseLabelLocale =
    locale === "pt" || locale === "en" || locale === "es"
      ? locale
      : bcp47ToShort(locale);
  return CASE_LABELS[type][shortLocale];
}

// ── React hooks ─────────────────────────────────────────────────────

/**
 * React hook — resolves the case-label for the active locale (read
 * from `useLanguageStore`). Re-renders on locale change automatically.
 * Prefer this in components; use `caseLabelFor` directly only from
 * non-React contexts.
 */
export function useCaseLabel(
  matterType: string | undefined,
  count?: number,
): string {
  const lang = useLanguageStore((s) => s.lang);
  return caseLabelFor(matterType, lang, count);
}

/**
 * React hook — returns both singular + plural for the active locale.
 * Mirrors `caseLabelForms` for component consumers.
 */
export function useCaseLabelForms(
  matterType: string | undefined,
): CaseLabelForm {
  const lang = useLanguageStore((s) => s.lang);
  return caseLabelForms(matterType, lang);
}
